/**
 * Coverage-gap display derivation (C4 — make coverage gaps visible early + resolvable).
 *
 * Every sync deal needs BOTH the master side AND the publishing side cleared (unless it's a
 * one-stop lane). The backend computes `deal.coverage_gaps` continuously
 * (services/licensing/coverage.py::_compute_coverage_gap_payload) and the read model emits it
 * un-sanitized for buyer AND seller viewers. This helper turns that raw payload into a small
 * render model the console strip / overview chip / finalize row / accept modal all share.
 *
 * Two classes of gap (owner decision 2026-07-14 — mirror of coverage.py's
 * DECLARABLE_OFF_PLATFORM_GAP_TYPES):
 *   • DECLARABLE — the rights genuinely aren't (all) on the platform, so the BUYER may attest they
 *     cleared the residual off-platform at accept (recorded, NOT verified):
 *       - `missing_holder_side`  → the side has no holder at all              (kind: "missing")
 *       - `coverage_incomplete`  → a holder covers only part of the share/territory (kind: "partial")
 *     A side-level declaration blankets that side's residual. The buyer enumerates nothing about
 *     other parties — the platform computes the residual; the buyer only checks "cleared off-platform".
 *   • HARD — an on-platform holder's record is incomplete/contested; fixed on the platform, never
 *     declared away:
 *       - `share_required`       → a confirmed holder never entered their share %
 *       - `coverage_uncertain`   → coverage for a holder side is flagged uncertain
 *
 * Pure — no React, no I/O. Unit-tested in src/test/coverage-display.test.ts.
 */
import { ALL_TERRITORIES, REGION_LABELS, type TerritoryRegion } from "@/data/territories";
// The ONE read-side worldwide vocabulary, shared with every other territory reader and pinned
// against `scope.py` in territory-scope.test.ts. NOT the renderer: see `humanizeTerritoryToken`
// for why this file keeps its own token->label tables.
import { TERRITORY_WORLDWIDE_LABEL, WORLDWIDE_TERRITORY_TOKENS } from "@/lib/territory-scope";
import type {
  LicenseCoverageGap,
  LicenseCoverageGapPayload,
  OffPlatformDeclaration,
} from "@/types/license-request";

export type CoverageSide = "master" | "publishing";

/** Gap kinds the BUYER can clear with an off-platform declaration. */
export type DeclarableGapKind = "missing" | "partial";

/** A declarable coverage gap on one side — buyer can attest it cleared off-platform. */
export interface DeclarableCoverageGap {
  side: CoverageSide;
  /** "missing" = no holder at all; "partial" = a holder covers only part of the share/territory. */
  kind: DeclarableGapKind;
  /**
   * SYSTEM-computed human context for the checkbox / strip row (buyer authors none of it):
   *   missing → "No holder added on the platform."
   *   partial → "45% cleared on the platform · 55% open." | "Not cleared on the platform for: …"
   */
  context: string;
}

/** A non-declarable ("hard") coverage gap — needs an on-platform fix. `overallocated` is a
 *  coverage_incomplete whose confirmed on-platform shares exceed 100% for a territory: a contested
 *  double-claim, NOT an off-platform residual, so it is hard (mirror of coverage.py). */
export interface HardCoverageGap {
  side: CoverageSide | string;
  kind: "share_required" | "coverage_uncertain" | "overallocated" | "unknown";
  /** What must happen on the platform, viewer-neutral. */
  message: string;
  clearanceUnitId?: string;
}

export interface CoverageStatus {
  /** Nothing to surface — no open gap AND no declared side. Surfaces that only render on a problem
   *  (the strip) gate on this. A declared-but-still-open side keeps this false so the acknowledgment
   *  renders. */
  complete: boolean;
  /** Declarable gaps the buyer has NOT yet declared — the RED, actionable set (missing OR partial),
   *  ordered master-before-publishing. Each carries its kind + system-computed context. Drives the
   *  strip rows and the accept modal's per-side checkboxes. */
  declarableGaps: DeclarableCoverageGap[];
  /** Ordered undeclared declarable SIDES (convenience for the accept gate + modal checkbox keys —
   *  the declaration is per-side, one checkbox per declarable side). Derived from `declarableGaps`. */
  declarableSides: CoverageSide[];
  /** Declarable sides the buyer HAS declared cleared off-platform (acknowledged; amber/neutral,
   *  NOT a red blocker). Deterministic master-before-publishing order. */
  declaredSides: CoverageSide[];
  /** HARD gaps (share % not entered / coverage uncertain) — an on-platform fix, never declarable. */
  hardGaps: HardCoverageGap[];
  /** One-line summary for the strip header / chip. "" when complete. */
  summary: string;
}

/** EXPORTED for the PRE-deal forecast chip
 *  (request-detail/preDealCoverageForecast.ts), which names the same two sides against the same
 *  evaluator payload. One vocabulary, so "Master" cannot become "master rights" on one surface. */
export function sideLabel(side: string): string {
  if (side === "master") return "Master";
  if (side === "publishing") return "Publishing";
  // Unknown right_type: title-case the slug so a future side reads sanely.
  return side ? side.charAt(0).toUpperCase() + side.slice(1) : "Coverage";
}

/**
 * Turn one backend territory token into something a reader can act on.
 *
 * BOTH HALVES OF THE NA/SA COLLISION ARE CLOSED HERE (regions 2026-08-05, bare codes 2026-08-06).
 * The `r:` prefix exists in the backend's storage scheme precisely because the region keys NA/SA
 * collide with the ISO country codes for Namibia and Saudi Arabia (territory_service.py's module
 * docstring), and `collapse_to_tokens` puts region tokens and bare codes in the SAME bucket list
 * (territory_service.py:134-154). So printing either side of that collision raw made one sentence
 * carry two readings of the same two letters:
 *   · region raw  — the live 'Demeanor' shortfall read "45% in UK, NA", where NA was North America.
 *   · code raw    — a holder scoped to Saudi Arabia against an ask of South America + SA read
 *                   "0% in South America; 50% in SA": 0% and 50% in what looks like one place.
 *
 * TWO VOCABULARIES, KEPT APART, because the collision is exactly that the same string means
 * different things at the two levels:
 *   · `r:` tokens → `REGION_LABELS`. src/data/territories.ts mirrors the backend's
 *     `REGION_COUNTRIES`, and test_territory_service.py parses that file and asserts the two stay in
 *     lock-step, so this key set cannot drift from the one the evaluator emits. The legacy `MENA`
 *     alias the backend accepts on input maps to the same region.
 *   · bare codes  → `COUNTRY_NAMES`, built from `ALL_TERRITORIES` (every country that belongs to a
 *     region — the same universe the evaluator expands `worldwide` to). Deliberately NOT
 *     `getTerritoryLabel`: its `resolveRegion` matches region KEYS first, so it turns a bare "SA"
 *     into "South America" — the same collision inverted. That is what the earlier "bare codes pass
 *     through untouched" rule was really guarding against, and it is a narrower objection than the
 *     rule it justified.
 *
 * A token NEITHER table knows still passes through untouched — including any future scheme. It must
 * not reach `Intl.DisplayNames`: that answers "Unknown Region" for structurally valid subtags like
 * "ZZ", and a confident wrong name is worse than a code.
 *
 * `worldwide` USED to be in that pass-through set and no longer reaches here at all: the caller
 * answers the whole-world question first, because it is a question about the LIST rather than about
 * a token. See `summarizeTerritoryTokens` (audit TERR-42).
 */
const REGION_TOKEN_ALIASES: Record<string, TerritoryRegion> = { MENA: "MEENA" };

const COUNTRY_NAMES: Map<string, string> = new Map(
  ALL_TERRITORIES.map((territory) => [territory.code.toUpperCase(), territory.name]),
);

function humanizeTerritoryToken(token: string): string {
  const t = token.trim();
  const stripped = t.replace(/^[a-z]:/i, "").trim();
  if (/^r:/i.test(t)) {
    const key = stripped.toUpperCase();
    const region = (REGION_TOKEN_ALIASES[key] ?? key) as TerritoryRegion;
    if (region in REGION_LABELS) return REGION_LABELS[region];
    // An unknown `r:` token is NOT a country — fall through to the code, never to a country name.
    return stripped || t;
  }
  return COUNTRY_NAMES.get(stripped.toUpperCase()) ?? (stripped || t);
}

/** Round to at most 2 decimals, dropping a trailing ".0".
 *  EXPORTED for the pre-deal forecast line — see `summarizeTerritoryTokens`. */
export function trimPct(n: number): number {
  return Math.round(n * 100) / 100;
}

function toPct(raw: unknown): number | null {
  if (typeof raw === "number") return raw;
  if (raw != null && !Number.isNaN(Number(raw))) return Number(raw);
  return null;
}

/** What to call a shortfall whose territory list names NOTHING — the state most easily mistaken for
 *  the one Phase 4 exists to fix, so it is named rather than inlined (audit TERR-01).
 *
 *  AN EMPTY LIST HERE IS NOT AN EMPTY GRANT. Everywhere else a missing territory value is a holder
 *  who never stated a scope, and reading that silence as "Worldwide" is the defect. This one
 *  describes a SHORTFALL — the hole, not the grant — and `territory_service._scope_tokens` returns
 *  `[]` deliberately when the ASK named no territory at all, precisely so the persisted blocker
 *  stops asserting "worldwide" about a scope nobody requested. The hole therefore covers whatever
 *  was asked for, and this says so.
 *
 *  It must NOT become "Territories not stated". On this line that is a rights record needing a fix,
 *  and it would send a seller looking for a holder to correct when the ask is what is unnamed.
 *
 *  Named on BOTH sides (`territory_display.TERRITORY_SHORTFALL_UNNAMED_LABEL`) and pinned to the
 *  Python in request-track-pre-deal-coverage.test.ts, because two readers of one stored bucket
 *  inventing two spellings of one sentence is exactly what four hand-written versions of "not
 *  stated" already did. */
export const TERRITORY_SHORTFALL_UNNAMED_LABEL = "the requested scope";

/** Humanize + cap a shortfall's territory-token list (backend region/country tokens).
 *
 *  EXPORTED (2026-08-05) for the PRE-deal coverage forecast line, which reads the SAME shortfall
 *  buckets from the SAME evaluator (`_compute_coverage_gap_payload`, reached pre-deal through
 *  services/licensing/forecast.py). A second copy of the region mapping + the 4-token cap is how the
 *  two surfaces would end up naming one territory hole two different ways.
 *
 *  It used to carry a claim that "the common partial case is a share shortfall (a numeric %), which
 *  never reaches here" — false, and load-bearingly so: EVERY pre-deal shortfall line routes its
 *  tokens through this function, and the numeric % is printed BESIDE the territories rather than
 *  instead of them (live 'Demeanor': "45% in United Kingdom, North America"). The tokens are the
 *  reader's only handle on WHERE the hole is, which is why naming the regions is worth the mapping.
 *
 *  Two token lists get an answer instead of a list of places: an empty one
 *  (`TERRITORY_SHORTFALL_UNNAMED_LABEL`, above) and one naming the whole world (below). */
export function summarizeTerritoryTokens(raw: unknown): string {
  const cleaned = Array.isArray(raw) ? raw.map((t) => String(t).trim()).filter(Boolean) : [];
  if (cleaned.length === 0) return TERRITORY_SHORTFALL_UNNAMED_LABEL;
  // THE WHOLE WORLD IS A FACT SOMEBODY WROTE DOWN, so it is said in the platform's word for it and
  // not in its storage bytes (audit TERR-42). `_scope_tokens` emits the literal lowercase token
  // `["worldwide"]` when the buyer's ask names the whole world, and this line rendered it verbatim
  // — "0% in worldwide" on the card — while `coverage.py::_format_coverage_shortfall`, the other
  // reader of the same stored bucket, said "Worldwide". That comment names this function as its
  // mirror; this is the half that makes it one.
  //
  // The synonym set is read-side only and wider than anything written today: a bucket carrying `ww`
  // predates the canonicalizer and still means the whole world, and printing "WW" states an ISO
  // code that does not exist. A synonym ANYWHERE collapses the whole list, matching
  // `territory_tokens_label`'s own `any(...)` rule — "Worldwide, France" would claim the whole
  // world and also one country.
  if (cleaned.some((token) => WORLDWIDE_TERRITORY_TOKENS.has(token.toLowerCase()))) {
    return TERRITORY_WORLDWIDE_LABEL;
  }
  const uniq = Array.from(new Set(cleaned.map(humanizeTerritoryToken)));
  if (uniq.length <= 4) return uniq.join(", ");
  return `${uniq.slice(0, 4).join(", ")} +${uniq.length - 4} more`;
}

/** True when a coverage_incomplete gap carries an `overallocated` shortfall (confirmed on-platform
 *  shares >100% for a territory) — a CONTESTED on-platform record, not an off-platform residual.
 *  Mirror of coverage.py::_has_overallocated_shortfall. */
function hasOverallocatedShortfall(gap: LicenseCoverageGap): boolean {
  const shortfalls = Array.isArray(gap.shortfalls) ? gap.shortfalls : [];
  return shortfalls.some(
    (s) => s && typeof s === "object" && String((s as Record<string, unknown>).reason ?? "") === "overallocated",
  );
}

/** System-computed context for a partial (coverage_incomplete) gap — the residual the platform saw
 *  open, narrated PER shortfall bucket (the evaluator emits per-bucket percentages, so a single
 *  covered_pct is NOT the side-wide figure). Never authored by a user. Overallocated buckets never
 *  reach here (routed to hard). Reads well after "{Side} side — ". */
function partialContext(gap: LicenseCoverageGap): string {
  const shortfalls = Array.isArray(gap.shortfalls) ? gap.shortfalls : [];
  const parts: string[] = [];
  for (const shortfall of shortfalls) {
    if (!shortfall || typeof shortfall !== "object") continue;
    const s = shortfall as Record<string, unknown>;
    const reason = String(s.reason ?? "");
    if (reason === "overallocated") continue; // contested → surfaced as a hard gap, not here
    const territories = summarizeTerritoryTokens(s.territories);
    if (reason === "underallocated") {
      const pct = toPct(s.covered_pct);
      parts.push(pct != null ? `${trimPct(pct)}% cleared for ${territories}` : `partly cleared for ${territories}`);
    } else {
      // uncovered / no_holder / any other residual → not cleared for those territories
      parts.push(`not cleared for ${territories}`);
    }
  }
  return parts.length ? parts.join("; ") : "partly cleared on the platform";
}

/** Hard-gap message for an over-allocation (contested on-platform double-claim). */
function overallocatedMessage(gap: LicenseCoverageGap): string {
  const side = sideLabel(String(gap.right_type ?? ""));
  return `${side}: on-platform rights shares exceed 100% for some territories — resolve the conflict on the platform before accepting.`;
}

/**
 * THE ONE CHIP LABEL FOR AN OVER-ALLOCATION, at both stages.
 *
 * Defined once and consumed by `coverageChipLabel` below (deal stage) AND by the pre-deal forecast
 * chip (request-detail/preDealCoverageForecast.ts). Before this the same state had two names: the
 * pre-deal chip said "Master shares exceed 100%" and the deal-stage chip said "Master coverage
 * incomplete" — the words a share SHORTFALL gets, for the opposite job. A shortfall asks the seller
 * to ADD a claim; an over-allocation asks them to REMOVE one, and a seller sent looking for a
 * missing holder will not find the duplicate. The `!deal` gate means the two chips can never
 * co-render, so this was never a contradiction on screen — it was one state with two vocabularies,
 * which is how a seller learns that the two stages are different products.
 */
export function overallocatedChipLabel(side: string): string {
  return `${sideLabel(side)} shares exceed 100%`;
}

/** Human message for a hard gap. Falls back to the backend `message`, then a scoped generic. */
function hardGapMessage(gap: LicenseCoverageGap): string {
  const side = sideLabel(String(gap.right_type ?? ""));
  const message = String(gap.message ?? "").trim();
  if (message) return message;
  if (gap.gap_type === "share_required") {
    return `${side}: enter the holder's rights share % to complete acceptance.`;
  }
  if (gap.gap_type === "coverage_uncertain") {
    return `${side}: coverage for a holder side is marked uncertain.`;
  }
  return `${side}: coverage issue.`;
}

const ORDER: CoverageSide[] = ["master", "publishing"];

/**
 * Derive the shared coverage render model from a deal's `coverage_gaps` payload and the buyer's
 * `off_platform_declarations`. Null / undefined / empty gaps read as COMPLETE.
 *
 * A declarable gap (missing OR partial) whose side the buyer DECLARED cleared off-platform is
 * reclassified from a red `declarableGaps` entry to an acknowledged `declaredSides` entry — the
 * strip/chip must treat it as resolved-by-declaration, not open. The backend still emits the gap
 * (nothing changed on the platform); the declaration lives alongside it on the deal.
 */
export function deriveCoverageStatus(
  deal:
    | {
        coverage_gaps?: LicenseCoverageGapPayload | null;
        off_platform_declarations?: OffPlatformDeclaration[] | null;
      }
    | null
    | undefined,
): CoverageStatus {
  const payload = deal?.coverage_gaps ?? null;
  const gaps: LicenseCoverageGap[] = Array.isArray(payload?.gaps) ? payload!.gaps! : [];

  const declaredSet = new Set<CoverageSide>();
  const declarations = Array.isArray(deal?.off_platform_declarations)
    ? deal!.off_platform_declarations!
    : [];
  for (const decl of declarations) {
    const side = String(decl?.right_type ?? "");
    if (side === "master" || side === "publishing") declaredSet.add(side);
  }

  const EMPTY: CoverageStatus = {
    complete: true,
    declarableGaps: [],
    declarableSides: [],
    declaredSides: [],
    hardGaps: [],
    summary: "",
  };
  if (gaps.length === 0) {
    // No gaps to reclassify — a declaration without a matching gap is a no-op here.
    return EMPTY;
  }

  // One declarable gap per side at most (a side either has no holder OR has holders covering only
  // part — never both). Track by side so a declared side is acknowledged, not surfaced red.
  const declarableBySide = new Map<CoverageSide, DeclarableCoverageGap>();
  const declaredMissingSet = new Set<CoverageSide>();
  const hardGaps: HardCoverageGap[] = [];

  for (const gap of gaps) {
    if (!gap || typeof gap !== "object") continue;
    const side = String(gap.right_type ?? "");
    const isSide = side === "master" || side === "publishing";

    // An over-allocation (confirmed on-platform shares >100%) is a contested double-claim, NOT an
    // off-platform residual — route it to HARD, mirroring is_declarable_off_platform_gap. Checked
    // before the declarable branch so a stray declaration can never reclassify a conflict as cleared.
    if (isSide && gap.gap_type === "coverage_incomplete" && hasOverallocatedShortfall(gap)) {
      hardGaps.push({
        side: side as CoverageSide,
        kind: "overallocated",
        message: overallocatedMessage(gap),
        clearanceUnitId:
          typeof gap.clearance_unit_id === "string" ? gap.clearance_unit_id : undefined,
      });
      continue;
    }

    if (
      isSide &&
      (gap.gap_type === "missing_holder_side" || gap.gap_type === "coverage_incomplete")
    ) {
      const coverageSide = side as CoverageSide;
      if (declaredSet.has(coverageSide)) {
        declaredMissingSet.add(coverageSide);
      } else {
        const kind: DeclarableGapKind =
          gap.gap_type === "missing_holder_side" ? "missing" : "partial";
        const context =
          kind === "missing" ? "No holder added on the platform." : partialContext(gap);
        // First declarable gap for the side wins (deterministic; there is at most one in practice).
        if (!declarableBySide.has(coverageSide)) {
          declarableBySide.set(coverageSide, { side: coverageSide, kind, context });
        }
      }
      continue;
    }

    if (gap.gap_type === "share_required" || gap.gap_type === "coverage_uncertain") {
      hardGaps.push({
        side: isSide ? (side as CoverageSide) : side,
        kind: gap.gap_type,
        message: hardGapMessage(gap),
        clearanceUnitId:
          typeof gap.clearance_unit_id === "string" ? gap.clearance_unit_id : undefined,
      });
      continue;
    }

    // Unknown / future gap kind — treat as HARD (never silently let the buyer declare away a gap we
    // don't understand). Surface its message so it isn't dropped.
    hardGaps.push({
      side: isSide ? (side as CoverageSide) : side,
      kind: "unknown",
      message: hardGapMessage(gap),
      clearanceUnitId:
        typeof gap.clearance_unit_id === "string" ? gap.clearance_unit_id : undefined,
    });
  }

  const declarableGaps = ORDER.filter((s) => declarableBySide.has(s)).map(
    (s) => declarableBySide.get(s)!,
  );
  const declarableSides = declarableGaps.map((g) => g.side);
  const declaredSides = ORDER.filter((s) => declaredMissingSet.has(s));

  const complete =
    declarableGaps.length === 0 && hardGaps.length === 0 && declaredSides.length === 0;

  return {
    complete,
    declarableGaps,
    declarableSides,
    declaredSides,
    hardGaps,
    summary: complete
      ? ""
      : buildSummary(declarableGaps, hardGaps, declaredSides),
  };
}

/** Compact one-liner for the strip header / chip. Leads with the RED open items (most actionable),
 *  and falls back to the acknowledged off-platform declaration when nothing is blocking. */
function buildSummary(
  declarableGaps: DeclarableCoverageGap[],
  hardGaps: HardCoverageGap[],
  declaredSides: CoverageSide[],
): string {
  const openCount = declarableGaps.length + hardGaps.length;
  if (openCount === 0) return declaredSummary(declaredSides);

  if (openCount === 1) {
    if (declarableGaps.length === 1) {
      const g = declarableGaps[0];
      return g.kind === "missing"
        ? `${sideLabel(g.side)} side not yet cleared`
        : `${sideLabel(g.side)} side only partly cleared`;
    }
    return hardGaps[0].message;
  }

  return `${openCount} coverage items to resolve before this deal can be accepted`;
}

/** Acknowledgment line for sides the buyer declared cleared off-platform. */
function declaredSummary(declaredSides: CoverageSide[]): string {
  if (declaredSides.length === 1) {
    return `${sideLabel(declaredSides[0])} cleared off-platform (buyer declaration)`;
  }
  if (declaredSides.length === 2) {
    return "Master and publishing cleared off-platform (buyer declaration)";
  }
  return "";
}

/**
 * Short coverage chip label for the coarse Overview card chip. Returns null when there is no open
 * coverage signal (declared-only or complete). Coarse by design — names the open side(s) only; the
 * console strip + accept modal carry the share/territory detail.
 *
 * ONE EXCEPTION TO "coarse": an over-allocation gets its own words (`overallocatedChipLabel`),
 * because "coverage incomplete" describes the opposite job. Still not per-holder — the strip owns
 * the contributor breakdown. The two-open-sides case stays "Rights not cleared" for the same
 * coarseness reason: with both sides open there is no room to name each one's kind, and both are in
 * fact not cleared.
 */
export function coverageChipLabel(status: CoverageStatus): string | null {
  const missingSides = new Set<CoverageSide>(
    status.declarableGaps.filter((g) => g.kind === "missing").map((g) => g.side),
  );
  const overallocatedSides = new Set<CoverageSide>();
  const openSides = new Set<CoverageSide>();
  for (const g of status.declarableGaps) openSides.add(g.side);
  for (const h of status.hardGaps) {
    if (h.side === "master" || h.side === "publishing") {
      openSides.add(h.side);
      if (h.kind === "overallocated") overallocatedSides.add(h.side);
    }
  }
  if (openSides.size === 0) return null;
  if (openSides.size === 2) return "Rights not cleared";
  const side = ORDER.find((s) => openSides.has(s))!;
  if (overallocatedSides.has(side)) return overallocatedChipLabel(side);
  return missingSides.has(side) ? `${sideLabel(side)} not cleared` : `${sideLabel(side)} coverage incomplete`;
}
